import { getAuthaltToken, AUTHALT_API_BASE } from './authaltService';

export { AUTHALT_API_BASE };
export const AUTHALT_ATMANTAN_API_BASE = 'https://authalt.bsynapse.com/atmantan';

const authaltRequest = async (baseUrl, endpoint, body = {}, options = {}) => {
  let authToken = await getAuthaltToken();

  if (!authToken) {
    throw new Error('Failed to get authalt authentication token');
  }

  const url = `${baseUrl}${endpoint}`;
  const defaultOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X_API_KEY': authToken,
      'User-Agent': 'Scheduler-App/1.0'
    },
    body: JSON.stringify(body)
  };

  let response = await fetch(url, { ...defaultOptions, ...options });

  if (response.status === 401) {
    console.log('Authalt API returned 401, refreshing token...');
    authToken = await getAuthaltToken(true);

    if (authToken) {
      defaultOptions.headers['X_API_KEY'] = authToken;
      response = await fetch(url, { ...defaultOptions, ...options });
    }
  }

  return response;
};

/**
 * Make an authenticated request to the authalt API
 * This function automatically fetches the token from the backend and adds it to the headers
 * 
 * @param {string} endpoint - The authalt API endpoint (e.g., '/guestlistdatav3')
 * @param {Object} body - The request body
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export const authaltFetch = async (endpoint, body = {}, options = {}) => {
  return authaltRequest(AUTHALT_API_BASE, endpoint, body, options);
};

/**
 * POST to authalt /atmantan/* endpoints (AIIMS guest tasks, package webhooks, etc.)
 */
export const authaltAtmantanFetch = async (endpoint, body = {}, options = {}) => {
  return authaltRequest(AUTHALT_ATMANTAN_API_BASE, endpoint, body, options);
};

/**
 * Fetch doctor-assigned guest tasks from AIIMS for itinerary display.
 * Requires both guestId and bookingId.
 */
export const fetchGuestTasks = async (guestId, bookingId) => {
  if (!guestId || !bookingId) return [];

  try {
    const response = await authaltAtmantanFetch('/guesttask', { guestId, bookingId });
    if (!response.ok) {
      console.warn(`Guest tasks API returned ${response.status}`);
      return [];
    }

    const json = await response.json();
    const data = json?.result?.data ?? json?.data ?? [];
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching guest tasks:', error);
    return [];
  }
};

/**
 * Make a GET request to the authalt API (if needed)
 * Most authalt endpoints use POST, but this is available if needed
 */
export const authaltGet = async (endpoint, options = {}) => {
  let authToken = await getAuthaltToken();
  
  if (!authToken) {
    throw new Error('Failed to get authalt authentication token');
  }

  const url = `${AUTHALT_API_BASE}${endpoint}`;
  
  const defaultOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X_API_KEY': authToken
    }
  };

  let response = await fetch(url, { ...defaultOptions, ...options });
  
  if (response.status === 401) {
    console.log('Authalt API returned 401, refreshing token...');
    authToken = await getAuthaltToken(true);
    
    if (authToken) {
      defaultOptions.headers['X_API_KEY'] = authToken;
      response = await fetch(url, { ...defaultOptions, ...options });
    }
  }
  
  return response;
};
